import PromiseCache from './promise-cache';
import { setLanguageBundle, updateLanguageBundle } from '@nackle/intl-tools';

export default function( umbrella ) {
    const fetch = umbrella.fetch;
    const cache = new PromiseCache();
    const cmxURL = umbrella.isLocalhost() ? 'https://qa.api.biw.cloud/v2/cmx' : `https://${ window.location.hostname }/api/v2/cmx`;
    const cypherURL = umbrella.isLocalhost() ? 'https://qa.api.biw.cloud/v1/cypher' : `https://${ window.location.hostname }/api/v1/cypher`;

    let currentLocale = 'en-US';
    let currentBundle = {};
    let currentBundleNames = [];

    let currentCrate = {};
    let currentCrateNames = [];

    async function getBundle( bundleName, locales ) {
        return await cache( `getBundle:${ bundleName }:${ locales.join( ':' ) }`, async () => {
            const res = await fetch(
                `${ cmxURL }/bundle/${ encodeURIComponent( bundleName ) }`,
                {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify( { locales } )
                }
            );

            const data = await res.json();

            if ( res.ok ) {
                return data;
            } else {
                throw data;
            }
        } )();
    }

    async function getBundleWithAttributes( bundleName, locales ) {
        return await cache( `getBundleAttributes:${ bundleName }:${ locales.join( ':' ) }`, async () => {
            const res = await fetch(
                `${ cmxURL }/bundle/${ encodeURIComponent( bundleName ) }`,
                {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify( { locales, returnStringAttributes: true } )
                }
            );

            const data = await res.json();

            if ( res.ok ) {
                return data;
            } else {
                throw data;
            }
        } )();
    }

    async function getCrate( crateName, locales ) {
        return await cache( `getCrate:${ crateName }:${ locales.join( ':' ) }`, async () => {
            const localeQueryString = locales.map( l => `localeCode[]=${ l }` ).join( '&' );
            const res = await fetch(
                `${ cypherURL }/crates?stackCode=${ crateName }&${ localeQueryString }`,
                {
                    method: 'GET',
                    headers: {
                        'content-type': 'application/json'
                    }
                }
            );
            const data = await res.json();

            if ( res.ok ) {
                return data;
            } else {
                throw data;
            }
        } )();
    }

    async function getMergedBundle( bundleNames, locale ) {
        let mergedBundle = currentBundle;

        if ( !locale ) {
            locale = getLocale();
        }
        currentLocale = locale;

        if ( !bundleNames ) {
            bundleNames = currentBundleNames;
        }
        currentBundleNames = [].concat( bundleNames );

        const bundleData = await Promise.all( bundleNames.map(
            bundleName => getBundle( bundleName, [ locale ] )
        ) );

        for ( let i = 0; i < bundleData.length; i++ ) {
            mergedBundle = {
                ...mergedBundle,
                ...bundleData[ i ].bundles[ locale ]
            };
        }
        currentBundle = mergedBundle;

        await setLanguageBundle( locale, mergedBundle );

        return currentBundle;
    }

    async function getMergedCrate( crateNames, locale ) {
        let mergedCrate = currentCrate;

        if ( !locale ) {
            locale = getLocale();
        }
        currentLocale = locale;

        if ( !crateNames ) {
            crateNames = currentCrateNames;
        }
        currentCrateNames = [].concat( crateNames );

        const crateData = await Promise.all( crateNames.map(
            crateName => getCrate( crateName, [ locale ] )
        ) );

        for ( let i = 0; i < crateData.length; i++ ) {
            Object.keys( crateData[ i ].crates ).forEach( crateStack => {
                mergedCrate = {
                    ...mergedCrate,
                    ...crateData[ i ].crates[ crateStack ][ locale ]
                };
            } );
        }
        currentCrate = mergedCrate;

        const combinedBundleAndCrate = {
            ...currentBundle,
            ...currentCrate
        };

        await setLanguageBundle( locale, combinedBundleAndCrate );

        return combinedBundleAndCrate;
    }

    async function getMergedAttributeBundle( bundleNames, locale ) {
        let mergedBundle = currentBundle;

        if ( !locale ) {
            locale = getLocale();
        }
        currentLocale = locale;

        if ( !bundleNames ) {
            bundleNames = currentBundleNames;
        }
        currentBundleNames = [].concat( bundleNames );

        const bundleData = await Promise.all( bundleNames.map(
            bundleName => getBundleWithAttributes( bundleName, [ locale ] )
        ) );
        for ( let i = 0; i < bundleData.length; i++ ) {
            mergedBundle = {
                ...mergedBundle,
                ...bundleData[ i ].bundles[ locale ]
            };
        }
        currentBundle = mergedBundle;

        await updateLanguageBundle( mergedBundle );

        return currentBundle;
    }

    async function getStringsWithAttributes( bundleName, keys = [], locale ) {
        if ( !locale ) {
            locale = getLocale();
        }

        return await cache( `getStringsWithAttributes:${ bundleName }:${ locale }`, async () => {
            const res = await fetch(
                `${ cmxURL }/projects/${ encodeURIComponent( bundleName ) }/locales/${ encodeURIComponent( locale ) }/attributes`,
                {
                    method: 'POST',
                    headers: {
                        'content-type': 'application/json'
                    },
                    body: JSON.stringify( { keys } )
                }
            );

            const data = await res.json();

            if ( res.ok ) {
                return data;
            } else {
                throw data;
            }
        } )();
    }

    async function getLocale() {
        return currentLocale;
    }

    async function setLocale( locale ) {
        await getMergedBundle( null, locale );
        return currentLocale;
    }

    umbrella.loadBundle = getMergedBundle;
    umbrella.loadCrate = getMergedCrate;
    umbrella.loadStringAttributeBundles = getMergedAttributeBundle;
    umbrella.getStringsWithAttributes = getStringsWithAttributes;
    umbrella.getLocale = getLocale;
    umbrella.setLocale = setLocale;
 }
